<template>
  <div>
    <main-navigation></main-navigation>

    <div class="tenant-dashboard">
      <!-- HEADER -->
      <div class="dashboard-header">
        <router-link to="/tenants" class="back-link">
          <i class="bi bi-arrow-left"></i>
          <span>Back to Tenants</span>
        </router-link>
        <h1>Tenant Profile</h1>
        <div class="header-actions">
          <button class="btn-secondary" @click="viewStatement">
            <i class="bi bi-receipt-cutoff"></i> View Statement
          </button>
          <button class="btn-primary" @click="editTenant">
            <i class="bi bi-pencil-square"></i> Edit Tenant
          </button>
        </div>
      </div>

      <!-- TENANT IDENTITY CARD -->
      <div class="identity-card">
        <div class="identity-avatar" :style="{ background: getGradient(tenant.gender) }">
          {{ getInitials(tenant.tenant_name) }}
        </div>
        <div class="identity-info">
          <h2>{{ tenant.tenant_name }}</h2>
          <div class="identity-badges">
            <span class="badge-id"><i class="bi bi-card-text"></i> {{ tenant.tenant_idnumber }}</span>
            <span class="badge-status" :class="tenant.status || 'active'">{{ tenant.status || 'Active' }}</span>
          </div>
          <div class="identity-contact">
            <span><i class="bi bi-telephone"></i> {{ tenant.tenant_phone || '—' }}</span>
            <span><i class="bi bi-gender-ambiguous"></i> {{ tenant.gender || '—' }}</span>
            <span><i class="bi bi-heart"></i> {{ tenant.marital_status || '—' }}</span>
          </div>
        </div>
        <div class="identity-metrics">
          <div class="metric">
            <span class="metric-label">Arrears</span>
            <span class="metric-value negative">{{ formatCurrency(tenant.arrears) }}</span>
          </div>
          <div class="metric">
            <span class="metric-label">Wallet</span>
            <span class="metric-value positive">{{ formatCurrency(tenant.wallet_amount) }}</span>
          </div>
          <div class="metric">
            <span class="metric-label">Total Paid</span>
            <span class="metric-value">{{ formatCurrency(totalPayments) }}</span>
          </div>
        </div>
      </div>

      <!-- QUICK STATS ROW -->
      <div class="stats-grid">
        <div class="stat-tile">
          <i class="bi bi-door-open"></i>
          <div>
            <span class="stat-tile-value">{{ units.length }}</span>
            <span class="stat-tile-label">Units Occupied</span>
          </div>
        </div>
        <div class="stat-tile">
          <i class="bi bi-receipt"></i>
          <div>
            <span class="stat-tile-value">{{ bills.length }}</span>
            <span class="stat-tile-label">Bills Generated</span>
          </div>
        </div>
        <div class="stat-tile">
          <i class="bi bi-cash"></i>
          <div>
            <span class="stat-tile-value">{{ payments.length }}</span>
            <span class="stat-tile-label">Payments Made</span>
          </div>
        </div>
        <div class="stat-tile">
          <i class="bi bi-calendar-check"></i>
          <div>
            <span class="stat-tile-value">{{ daysSinceMoveIn }}</span>
            <span class="stat-tile-label">Days with us</span>
          </div>
        </div>
      </div>

      <!-- TWO COLUMN LAYOUT FOR MAIN CONTENT -->
      <div class="two-column-layout">
        <!-- LEFT COLUMN - UNITS -->
        <div class="column">
          <div class="column-header">
            <h3><i class="bi bi-door-open"></i> Occupied Units</h3>
            <span class="column-badge">{{ units.length }} unit{{ units.length !== 1 ? 's' : '' }}</span>
          </div>

          <div v-if="!units.length" class="empty-tile">
            <i class="bi bi-house-x"></i>
            <p>No units assigned</p>
          </div>

          <div v-else class="units-container">
            <div v-for="unit in units" :key="unit.id" class="unit-tile">
              <div class="unit-tile-header">
                <div>
                  <span class="unit-number">{{ unit.unit_number }}</span>
                  <span class="unit-type">{{ unit.unit_type || 'Standard' }}</span>
                </div>
                <span :class="['unit-status', unit.status]">{{ unit.status }}</span>
              </div>

              <div class="unit-tile-body">
                <div class="info-row">
                  <i class="bi bi-building"></i>
                  <span><strong>House:</strong> {{ unit.House?.name || '—' }}</span>
                </div>
                <div class="info-row">
                  <i class="bi bi-geo-alt"></i>
                  <span><strong>Location:</strong> {{ unit.House?.location || '—' }}</span>
                </div>

                <div class="divider"></div>

                <div class="financial-mini-grid">
                  <div class="mini-item">
                    <span class="mini-label">Rent</span>
                    <span class="mini-value">{{ formatCurrency(unit.rent_amount) }}</span>
                  </div>
                  <div class="mini-item">
                    <span class="mini-label">Deposit</span>
                    <span class="mini-value">{{ formatCurrency(unit.deposit_amount) }}</span>
                  </div>
                  <div class="mini-item">
                    <span class="mini-label">Service</span>
                    <span class="mini-value">{{ formatCurrency(unit.service_charge) }}</span>
                  </div>
                  <div class="mini-item">
                    <span class="mini-label">Other</span>
                    <span class="mini-value">{{ formatCurrency(unit.other_charges) }}</span>
                  </div>
                </div>

                <div class="meter-mini" v-if="unit.water_meter_readings || unit.electricity_meter_readings">
                  <span v-if="unit.water_meter_readings" class="meter-chip">
                    <i class="bi bi-droplet"></i> {{ unit.water_meter_readings }}
                  </span>
                  <span v-if="unit.electricity_meter_readings" class="meter-chip">
                    <i class="bi bi-lightning"></i> {{ unit.electricity_meter_readings }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN - BILLS & PAYMENTS -->
        <div class="column">
          <!-- BILLS SECTION -->
          <div class="column-header">
            <h3><i class="bi bi-receipt"></i> Recent Bills</h3>
            <span class="column-badge">{{ bills.length }} total</span>
          </div>

          <div v-if="!bills.length" class="empty-tile">
            <i class="bi bi-receipt"></i>
            <p>No bills found</p>
          </div>

          <div v-else class="transaction-list">
            <div v-for="bill in bills.slice(0, 3)" :key="bill.id" class="transaction-item">
              <div class="transaction-icon" :class="getBillType(bill)">
                <i :class="getBillIcon(bill)"></i>
              </div>
              <div class="transaction-content">
                <div class="transaction-header">
                  <span class="transaction-title">{{ bill.bill_name || 'Bill' }}</span>
                  <span class="transaction-status" :class="bill.status">{{ bill.status || 'Pending' }}</span>
                </div>
                <div class="transaction-meta">
                  <span class="transaction-date"><i class="bi bi-calendar3"></i> {{ formatDate(bill.createdAt) }}</span>
                  <span class="transaction-amount">{{ formatCurrency(bill.amount) }}</span>
                </div>
                <p class="transaction-desc">{{ truncateText(bill.description, 40) }}</p>
              </div>
            </div>
            <router-link v-if="bills.length > 3" to="#" class="view-all-link">
              View all {{ bills.length }} bills <i class="bi bi-arrow-right"></i>
            </router-link>
          </div>

          <!-- PAYMENTS SECTION -->
          <div class="column-header" style="margin-top: 20px;">
            <h3><i class="bi bi-cash"></i> Recent Payments</h3>
            <span class="column-badge">{{ payments.length }} total</span>
          </div>

          <div v-if="!payments.length" class="empty-tile">
            <i class="bi bi-cash-stack"></i>
            <p>No payments recorded</p>
          </div>

          <div v-else class="transaction-list">
            <div v-for="payment in payments.slice(0, 3)" :key="payment.id" class="transaction-item">
              <div class="transaction-icon payment">
                <i class="bi bi-check-circle-fill"></i>
              </div>
              <div class="transaction-content">
                <div class="transaction-header">
                  <span class="transaction-title">Payment</span>
                  <span class="payment-amount-badge">{{ formatCurrency(payment.TransAmount) }}</span>
                </div>
                <div class="transaction-meta">
                  <span class="transaction-date"><i class="bi bi-calendar3"></i> {{ formatDate(payment.payment_date) }}</span>
                  <span class="transaction-ref"><i class="bi bi-upc-scan"></i> {{ payment.TransID }}</span>
                </div>
                <div class="transaction-payer" v-if="payment.FirstName">
                  <i class="bi bi-person"></i> {{ payment.FirstName }} {{ payment.MiddleName || '' }} {{ payment.LastName || '' }}
                </div>
              </div>
            </div>
            <router-link v-if="payments.length > 3" to="#" class="view-all-link">
              View all {{ payments.length }} payments <i class="bi bi-arrow-right"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  data() {
    return {
      tenantId: null,
      tenant: {},
      units: [],
      bills: [],
      payments: [],
    };
  },

  async mounted() {
    this.tenantId = this.$route.params.tenant_id;
    await this.loadAllData();
  },

  computed: {
    totalPayments() {
      return this.payments.reduce((sum, payment) => {
        return sum + Number(payment.TransAmount);
      }, 0);
    },

    daysSinceMoveIn() {
      if (!this.tenant.move_in_date) return 0;
      const moveIn = new Date(this.tenant.move_in_date);
      const today = new Date();
      const diffTime = Math.abs(today - moveIn);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  },

  methods: {
    async loadAllData() {
      await Promise.all([
        this.getTenant(),
        this.getUnits(),
        this.getBills(),
        this.getPayments()
      ]);
    },

    async getTenant() {
      try {
        const response = await apiFetch("/tenants");
        const tenant = response.find((t) => t.id == this.tenantId);
        if (tenant) {
          this.tenant = tenant;
        }
      } catch (error) {
        console.error("Tenant fetch error", error);
      }
    },

    async getUnits() {
      try {
        const response = await apiFetch("/units");
        this.units = response.filter((u) => u.tenant_id == this.tenantId);
      } catch (error) {
        console.error("Unit fetch error", error);
      }
    },

    async getBills() {
      try {
        const response = await apiFetch("/get/bills");
        this.bills = response.data.filter(
          (bill) => bill.tenant_id == this.tenantId,
        );
      } catch (error) {
        console.error("Bills fetch error", error);
      }
    },

    async getPayments() {
      try {
        const response = await apiFetch("/get/payments");
        this.payments = response.data.filter(
          (payment) => payment.tenant_id == this.tenantId,
        );
      } catch (error) {
        console.error("Payments fetch error", error);
      }
    },

    getInitials(name) {
      if (!name) return 'T';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },

    getGradient(gender) {
      const gradients = {
        'Male': 'linear-gradient(145deg, #3b82f6, #2563eb)',
        'Female': 'linear-gradient(145deg, #ec4899, #db2777)',
        'default': 'linear-gradient(145deg, #8b5cf6, #7c3aed)'
      };
      return gradients[gender] || gradients.default;
    },

    getBillType(bill) {
      if (bill.bill_name?.toLowerCase().includes('water')) return 'water';
      if (bill.bill_name?.toLowerCase().includes('electric')) return 'electric';
      return 'default';
    },

    getBillIcon(bill) {
      const type = this.getBillType(bill);
      const icons = {
        'water': 'bi bi-droplet',
        'electric': 'bi bi-lightning',
        'default': 'bi bi-receipt'
      };
      return icons[type];
    },

    formatCurrency(value) {
      if (!value && value !== 0) return 'KES 0';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    editTenant() {
      this.$router.push(`/edit-tenant/${this.tenantId}`);
    },

    viewStatement() {
      this.$router.push(`/tenant/${this.tenantId}/statement`);
    }
  }
};
</script>

<style scoped>
.tenant-dashboard {
  padding: 24px 32px;
  background: var(--dash-bg);
  min-height: calc(100vh - 80px);
  font-family: var(--dash-font);
}

/* Dashboard Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 16px 24px;
  border-radius: var(--dash-radius-lg);
  box-shadow: 0 4px 6px -2px rgba(0,0,0,0.05);
}

.back-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-muted);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: var(--dash-pill);
  transition: all 0.2s;
}

.back-link:hover {
  background: var(--dash-bg-alt);
  color: var(--dash-text);
}

.dashboard-header h1 {
  font-size: 20px;
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-primary {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--dash-pill);
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: var(--dash-shadow);
}

.btn-primary:hover {
  background: var(--dash-primary-hover);
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-secondary {
  background: var(--dash-primary-soft, #eef2ff);
  color: var(--dash-primary-ink, var(--dash-primary));
  border: 1px solid var(--dash-primary);
  padding: 10px 20px;
  border-radius: var(--dash-pill);
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: var(--dash-primary);
  color: white;
  transform: translateY(-2px);
}

/* Identity Card */
.identity-card {
  background: white;
  border-radius: var(--dash-radius-xl);
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 8px 20px -4px rgba(0,0,0,0.08);
  border: 1px solid rgba(0,0,0,0.02);
}

.identity-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
  color: white;
  box-shadow: var(--dash-shadow);
  flex-shrink: 0;
}

.identity-info {
  flex: 2;
}

.identity-info h2 {
  font-size: 24px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 8px 0;
}

.identity-badges {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.badge-id {
  background: var(--dash-bg-alt);
  padding: 4px 12px;
  border-radius: var(--dash-pill);
  font-size: 13px;
  color: var(--dash-text);
  display: flex;
  align-items: center;
  gap: 6px;
}

.badge-status {
  padding: 4px 12px;
  border-radius: var(--dash-pill);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-status.active {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.badge-status.inactive {
  background: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

.identity-contact {
  display: flex;
  gap: 20px;
  font-size: 14px;
  color: var(--dash-text);
}

.identity-contact i {
  color: var(--dash-faint);
  margin-right: 6px;
}

.identity-metrics {
  display: flex;
  gap: 24px;
  background: var(--dash-surface-2);
  padding: 16px 24px;
  border-radius: var(--dash-radius-lg);
  flex-shrink: 0;
}

.metric {
  text-align: center;
  min-width: 100px;
}

.metric-label {
  display: block;
  font-size: 12px;
  color: var(--dash-muted);
  margin-bottom: 4px;
}

.metric-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
}

.metric-value.negative {
  color: var(--dash-danger);
}

.metric-value.positive {
  color: var(--dash-success);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-tile {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 4px 10px -2px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.02);
  transition: all 0.2s;
}

.stat-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px -4px rgba(0,0,0,0.1);
}

.stat-tile i {
  font-size: 1.75rem;
  color: var(--dash-primary-ink);
  background: var(--dash-primary-soft);
  padding: 10px;
  border-radius: var(--dash-radius);
}

.stat-tile-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--dash-ink);
  line-height: 1.2;
}

.stat-tile-label {
  font-size: 12px;
  color: var(--dash-muted);
}

/* Two Column Layout */
.two-column-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.column {
  background: white;
  border-radius: var(--dash-radius-xl);
  padding: 20px;
  box-shadow: 0 8px 20px -4px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.02);
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--dash-bg-alt);
}

.column-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.column-header h3 i {
  color: var(--dash-primary);
}

.column-badge {
  background: var(--dash-bg-alt);
  padding: 4px 12px;
  border-radius: var(--dash-pill);
  font-size: 12px;
  color: var(--dash-text);
}

/* Empty Tile */
.empty-tile {
  text-align: center;
  padding: 40px 20px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-lg);
  border: 2px dashed var(--dash-line);
}

.empty-tile i {
  font-size: 2rem;
  color: var(--dash-line-strong);
  margin-bottom: 12px;
}

.empty-tile p {
  color: var(--dash-muted);
  margin: 0;
  font-size: 14px;
}

/* Units Container */
.units-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;
}

.units-container::-webkit-scrollbar {
  width: 4px;
}

.units-container::-webkit-scrollbar-track {
  background: var(--dash-bg-alt);
  border-radius: var(--dash-radius-xs);
}

.units-container::-webkit-scrollbar-thumb {
  background: var(--dash-line-strong);
  border-radius: var(--dash-radius-xs);
}

.unit-tile {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-lg);
  padding: 16px;
  border: 1px solid var(--dash-line);
  transition: all 0.2s;
}

.unit-tile:hover {
  border-color: var(--dash-primary);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.unit-tile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.unit-number {
  font-size: 16px;
  font-weight: 700;
  color: var(--dash-ink);
  background: var(--dash-line);
  padding: 4px 10px;
  border-radius: var(--dash-radius-xs);
  margin-right: 8px;
}

.unit-type {
  font-size: 13px;
  color: var(--dash-muted);
}

.unit-status {
  padding: 2px 10px;
  border-radius: var(--dash-pill);
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.unit-status.occupied {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.unit-status.vacant {
  background: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--dash-text);
  margin-bottom: 6px;
}

.info-row i {
  color: var(--dash-faint);
  width: 16px;
}

.divider {
  height: 1px;
  background: var(--dash-line);
  margin: 12px 0;
}

.financial-mini-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.mini-item {
  text-align: center;
  background: white;
  padding: 8px;
  border-radius: var(--dash-radius-sm);
  border: 1px solid var(--dash-line);
}

.mini-label {
  display: block;
  font-size: 10px;
  color: var(--dash-muted);
  margin-bottom: 2px;
}

.mini-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--dash-ink);
}

.meter-mini {
  display: flex;
  gap: 10px;
  background: var(--dash-bg-alt);
  padding: 8px 12px;
  border-radius: var(--dash-pill);
}

.meter-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--dash-text);
}

.meter-chip i {
  color: var(--dash-primary);
}

/* Transaction List */
.transaction-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
}

.transaction-list::-webkit-scrollbar {
  width: 4px;
}

.transaction-list::-webkit-scrollbar-track {
  background: var(--dash-bg-alt);
  border-radius: var(--dash-radius-xs);
}

.transaction-list::-webkit-scrollbar-thumb {
  background: var(--dash-line-strong);
  border-radius: var(--dash-radius-xs);
}

.transaction-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
  transition: all 0.2s;
}

.transaction-item:hover {
  border-color: var(--dash-primary);
  background: white;
}

.transaction-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--dash-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.transaction-icon.water {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.transaction-icon.electric {
  background: var(--dash-warning-soft);
  color: var(--dash-warning-ink);
}

.transaction-icon.default {
  background: var(--dash-line);
  color: var(--dash-text);
}

.transaction-icon.payment {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.transaction-content {
  flex: 1;
}

.transaction-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.transaction-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--dash-ink);
}

.transaction-status {
  padding: 2px 8px;
  border-radius: var(--dash-pill);
  font-size: 10px;
  font-weight: 600;
}

.transaction-status.paid {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.transaction-status.pending {
  background: var(--dash-warning-soft);
  color: var(--dash-warning-ink);
}

.payment-amount-badge {
  font-weight: 700;
  color: var(--dash-success);
  font-size: 14px;
}

.transaction-meta {
  display: flex;
  gap: 16px;
  font-size: 11px;
  color: var(--dash-muted);
  margin-bottom: 4px;
}

.transaction-meta i {
  margin-right: 2px;
}

.transaction-desc {
  font-size: 11px;
  color: var(--dash-text);
  margin: 0;
  line-height: 1.4;
}

.transaction-payer {
  font-size: 11px;
  color: var(--dash-primary-ink);
  background: var(--dash-primary-soft);
  padding: 2px 8px;
  border-radius: var(--dash-pill);
  display: inline-block;
}

.view-all-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-pill);
  font-size: 13px;
  color: var(--dash-primary);
  text-decoration: none;
  margin-top: 8px;
  border: 1px solid var(--dash-line);
  transition: all 0.2s;
}

.view-all-link:hover {
  background: var(--dash-primary-soft);
  border-color: var(--dash-primary-ink);
}

/* Responsive */
@media (max-width: 1200px) {
  .two-column-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .tenant-dashboard {
    padding: 20px;
  }

  .identity-card {
    flex-direction: column;
    text-align: center;
  }

  .identity-contact {
    justify-content: center;
    flex-wrap: wrap;
  }

  .identity-metrics {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .dashboard-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .btn-secondary,
  .header-actions .btn-primary {
    flex: 1;
    justify-content: center;
  }

  .identity-metrics {
    flex-wrap: wrap;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .transaction-meta {
    flex-direction: column;
    gap: 4px;
  }
}
</style>